/** Личный набор владельца. Суммы и ID провайдера остаются строками. */
import { apiFetch, query } from "@/lib/api";

export type PersonalAccount = { id: number; provider: "okx" | "bybit" | "bangkok" | "kasikorn"; account_key: string; name: string };
export type PersonalCategory = { id: number; account_id: number; name: string; sort_order: number };
export type PersonalStatement = {
  id: number;
  account_id: number;
  period_start: string;
  period_end: string;
  timezone: string;
  currency: string;
  count: number;
  pages: number;
  parser_version: string;
  summary: { spend: string; refund: string; fees: string; net: string; rewards: string };
};
export type PersonalOperation = {
  id: number;
  account_id: number;
  transaction_id: string;
  date: string;
  timezone: string;
  merchant: string;
  amount: string;
  currency: string;
  original_amount: string | null;
  original_currency: string | null;
  category_id: number | null;
  category_name: string | null;
  category_source: "unassigned" | "rule" | "manual";
  suggested: boolean;
  review_required: boolean;
  note: string;
};
export type PersonalOperationPage = {
  items: PersonalOperation[];
  total: number;
  offset: number;
  limit: number;
  totals: { currency: string; amount: string }[];
};
export type PersonalFilter = {
  account_id?: string;
  statement_id?: string;
  since?: string;
  until?: string;
  review_required?: "true" | "false";
  amount_min?: string;
  amount_max?: string;
  offset: number;
  limit: number;
};
export type PersonalImport = { statement_id: number; inserted: number; existing: number; duplicate: boolean };

export function loadPersonalAccounts(signal?: AbortSignal) {
  return apiFetch<PersonalAccount[]>("/api/personal/accounts", signal ? { signal } : undefined);
}
export function loadPersonalCategories(accountId: number, signal?: AbortSignal) {
  return apiFetch<PersonalCategory[]>(`/api/personal/categories${query({ account_id: accountId })}`, signal ? { signal } : undefined);
}
export function createPersonalCategory(accountId: number, name: string) {
  return apiFetch<PersonalCategory>("/api/personal/categories", { method: "POST", body: JSON.stringify({ account_id: accountId, name }) });
}
export function loadPersonalStatements(accountId: string, signal?: AbortSignal) {
  return apiFetch<PersonalStatement[]>(`/api/personal/statements${query({ account_id: accountId })}`, signal ? { signal } : undefined);
}
export function loadPersonalOperations(filter: PersonalFilter, signal?: AbortSignal) {
  return apiFetch<PersonalOperationPage>(`/api/personal/operations${query(filter)}`, signal ? { signal } : undefined);
}
export function uploadPersonalStatement(accountKey: string, file: File, provider: "okx" | "bybit" = "okx") {
  const body = new FormData();
  body.set("account_key", accountKey);
  body.set("file", file);
  body.set("provider", provider);
  return apiFetch<PersonalImport>("/api/personal/statements", { method: "POST", body });
}
export function setPersonalCategory(operationId: number, categoryId: number | null) {
  return apiFetch<PersonalOperation>(`/api/personal/operations/${operationId}`, {
    method: "PATCH", body: JSON.stringify({ category_id: categoryId, review_required: false }),
  });
}
export function personalStatementFileUrl(id: number) {
  return `/api/personal/statements/${id}/file`;
}

/** Точное отображение decimal без потери разрядов через Number. */
export function personalMoney(value: string): string {
  const negative = value.startsWith("-");
  const [whole = "0", fraction = ""] = value.replace(/^[+-]/, "").split(".");
  const decimals = fraction.replace(/0+$/, "").padEnd(2, "0");
  const nonzero = /[1-9]/.test(whole + fraction);
  return `${nonzero ? (negative ? "−" : "+") : ""}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0")},${decimals}`;
}
